import { useCallback, useEffect, useState } from "react"

import { useLatestRequestId } from "../../../../hooks/use-latest-request-id.ts"
import { useRefetchOnFocus } from "../../../../hooks/use-refetch-on-focus.ts"
import { getEventDetails } from "../../../Calendar/api/services.ts"
import { type Event } from "../../../Calendar/types/types.ts"

interface UseActivityEventResult {
  event: Event | null
  loading: boolean
  error: boolean
  refetch: () => void
}

/**
 * The lead-embedded event (`lead.lead_calendar_events`) has no address or
 * meeting link, so the popover fetches the full Calendar event instead —
 * only while it is open, never eagerly for a table full of rows.
 */
export function useActivityEvent(
  eventId: string | null | undefined,
  open: boolean,
  // A write-only consumer (StandaloneEventEditModal) has no use for the
  // tab-focus/30s poll this hook exists for — that's built for a passive
  // read-only Quick View catching an out-of-band RSVP answer, and buys an
  // actively-editing caller nothing but a wasted request. Defaults true so
  // every existing (read-only) caller is unaffected.
  pollOnFocus = true
): UseActivityEventResult {
  const [event, setEvent] = useState<Event | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)

  // Every fetch — the open-triggered one, a silent tab-focus refresh, and a
  // manual `refetch()` — claims the next id and only applies its response if
  // it's still the most recent claim when the response lands. Closing the
  // popover or the eventId changing invalidates it (see the effects below),
  // so a slow response for an event the user already navigated away from is
  // dropped instead of clobbering whatever is now on screen.
  const { claim, isCurrent, invalidate } = useLatestRequestId()

  // A second, independent instance: which claimed id, if any, is the one a
  // *non-silent* fetch raised the spinner for. A silent refresh never claims
  // this one, so if it ends up superseding a still-pending non-silent fetch
  // (`isCurrent` above goes false for it), that fetch's own `.finally` —
  // gated on this instance instead — is what still clears `loading`;
  // otherwise a stranded `loading: true` would sit forever with nothing
  // left to turn it off (an alt-tab mid-open-fetch is not a contrived
  // timing window).
  const loading_ = useLatestRequestId()

  // `fetchEvent` itself only re-claims (and so only invalidates the previous
  // claim) when it actually reaches `claim()` below — which it skips whenever
  // `eventId` is falsy. An eventId going truthy -> falsy while `open` stays
  // true would otherwise leave a still-in-flight request for the old id
  // "current," so a slow response for an event the row no longer links to
  // could still land and populate `event`. Unconditional on any id change,
  // mirroring CalendarPageActions's own `selectedEventId`-keyed effect.
  // Also clears any already-fetched `event` for the case fetchEvent's own
  // reset (setEvent(null) on a fresh non-silent fetch) never runs at all —
  // its early `if (!eventId) return` skips that reset the same way it skips
  // claim()/invalidate().
  useEffect(() => {
    invalidate()
    loading_.invalidate()
    if (!eventId) {
      setEvent(null)
      setError(false)
      setLoading(false)
    }
  }, [eventId, invalidate, loading_])

  const fetchEvent = useCallback(
    (silent = false) => {
      if (!eventId) return
      const requestId = claim()
      const loadingRequestId = silent ? null : loading_.claim()
      if (!silent) {
        setLoading(true)
        setError(false)
        setEvent(null)
      }

      getEventDetails(eventId)
        .then((response) => {
          if (!isCurrent(requestId)) return
          // Unlike the CRM endpoints, the calendar event detail route answers
          // with the plain Event object, not the {status, details} envelope —
          // `calendar.thunks.ts`'s own fetchEventDetails reads it the same way,
          // despite the service layer's (stale) BaseApiResponse<Event> typing.
          const data = response.data as unknown as Event | undefined
          if (response.ok && data?.id) {
            // Clears a stale `error` left by an earlier non-silent failure —
            // otherwise a silent refresh that succeeds after that failure
            // populates `event` but leaves the error/retry state on screen,
            // since silent fetches never touch `error` on their own failure
            // path below (deliberately, to stay non-disruptive).
            setError(false)
            setEvent(data)
          } else if (!silent) {
            setError(true)
          }
        })
        .finally(() => {
          if (
            loadingRequestId !== null &&
            loading_.isCurrent(loadingRequestId)
          ) {
            setLoading(false)
          }
        })
    },
    [eventId, claim, isCurrent, loading_]
  )

  useEffect(() => {
    if (!open) {
      invalidate()
      loading_.invalidate()
      setEvent(null)
      setError(false)
      setLoading(false)
      return
    }
    fetchEvent()
  }, [open, fetchEvent, invalidate, loading_])

  // A response for a request that outlives the component itself (the row it
  // belongs to unmounts — a table refetch/filter, not just a close) must not
  // call setState on the way out.
  useEffect(
    () => () => {
      invalidate()
      loading_.invalidate()
    },
    [invalidate, loading_]
  )

  useRefetchOnFocus(
    useCallback(() => fetchEvent(true), [fetchEvent]),
    open && pollOnFocus
  )

  // Wrapped (not the bare `fetchEvent` reference) so a caller that binds
  // `refetch` directly to an event handler — the popover's `onClick`, e.g. —
  // can't have a click event read as a truthy `silent`; `useCallback` keeps
  // it referentially stable across renders the way the bare `fetchEvent`
  // reference used to be, for any consumer that puts it in a dependency array.
  const refetch = useCallback(() => fetchEvent(), [fetchEvent])

  return { event, loading, error, refetch }
}
